import { useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { Button } from "@inkblot-therapy/squid"
import { useGetReferralById } from "../../hooks/useGetReferralById"
import { Alert } from "../Alert"
import { ReferralReviewForm } from "./ReferralReviewForm"
import { useFlags } from "launchdarkly-react-client-sdk"
import { FeatureFlagConfig } from "../../types/FeatureFlags"

interface Props {
  edit?: boolean
}

export function ReferralReview({ edit }: Props) {
  const [error, setError] = useState<string | undefined>(undefined)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { referralId } = useParams<{ referralId: string }>()
  const { useReferralVersion2 } = useFlags<FeatureFlagConfig>()
  const { data, error: fetchError } = useGetReferralById(
    referralId ?? "",
    useReferralVersion2,
  )

  const referral = data?.referral
  const form = data?.form
  const referral_status = data?.referral_status
  const adminMeta = data?.admin
  const locationState = location.state as { page?: string | null } | null

  const navigateToManage = useCallback(() => {
    const lastPage = locationState?.page

    if (lastPage) {
      navigate(`/manage?page=${lastPage}`)
    } else {
      navigate("/manage")
    }
  }, [locationState?.page, navigate])

  if (!referral || !form) {
    if (error || fetchError) {
      return (
        <div className="px-28 py-12">
          <Alert status="critical" text={t("admin.error.defaultError")} />
        </div>
      )
    }

    return null
  }

  return (
    <div className="px-28 py-12 [font-size:var(--alias-font-body-sm-base-font-size)]">
      <div className="flex items-center justify-between w-full">
        <p className="mr-8 [font-family:var(--alias-font-display-lg-font-family)] [font-size:var(--alias-font-display-lg-font-size)]">
          {t("admin.headers.referralForm")}
        </p>
        <Button
          variant="outlined"
          leftIcon="ArrowLeft"
          onClick={() => navigateToManage()}
        >
          {t("admin.buttons.backToDashboard")}
        </Button>
      </div>
      {error?.length ? (
        <Alert status="critical" text={t("admin.error.defaultError")} />
      ) : null}
      <div className="flex mt-12 items-start">
        <ReferralReviewForm
          key={referralId}
          form={form}
          defaultValues={referral}
          referralId={referralId}
          edit={edit}
          referral_status={referral_status}
          adminMeta={adminMeta}
          setError={setError}
        />
      </div>
    </div>
  )
}
